import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Slider,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Paper,
  IconButton,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  FormControlLabel,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Key as KeyIcon,
  Code as CodeIcon,
  Psychology as PsychologyIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { ALL_OPENAI_MODELS, getModelLabel } from '../../constants/openAIModels';
import { getMappedInDirections, removeSSMLTags } from '../../services/mappedInDirectionsService';

const EnhancedTextDirectionConfig = ({ userId, buttonColor = '#000000', showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showTestSection, setShowTestSection] = useState(false);
  
  // Configuration state
  const [config, setConfig] = useState({
    apiKeySource: 'admin',
    adminApiKeyId: '',
    customApiKey: '',
    model: 'gpt-4o-mini',
    customPrompt: '',
    temperature: 0.8,
    enabled: true,
  });

  // Available admin keys
  const [adminKeys, setAdminKeys] = useState([]);

  // Test state
  const [sampleDirections, setSampleDirections] = useState(
    'Leave the PROTO Hologram Display and turn left. Walk forward 50 feet. Turn right at the Water Feature. Continue straight. Take Escalator 27 down to Level 4. Take Escalator 20 down to Level 3. Turn left at the Information Counter. You have arrived at your destination.'
  );
  const [testResult, setTestResult] = useState(null);

  // Location search state
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [fetchingDirections, setFetchingDirections] = useState(false);
  const [mappedInSettings, setMappedInSettings] = useState(null);

  // Fetch configuration and admin keys
  useEffect(() => {
    if (userId) {
      fetchConfig();
      fetchAdminKeys();
      fetchLocations();
      fetchMappedInSettings();
    }
  }, [userId]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/wayfinder/enhanced-text-config/${userId}`);
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      showToast?.('Failed to load configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminKeys = async () => {
    try {
      const response = await axios.get('/api/wayfinder/enhanced-text-config/admin-keys/available');
      if (response.data.success) {
        setAdminKeys(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching admin keys:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await axios.get(`/api/wayfinder/get-locations/${userId}`);
      if (response.data.success) {
        setLocations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      showToast?.('Failed to load locations', 'error');
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchMappedInSettings = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/mappedin-settings/${userId}`);
      if (response.data.success && response.data.data) {
        setMappedInSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching MappedIn settings:', error);
    }
  };

  const handleFetchMappedInDirections = async () => {
    if (!selectedLocation) {
      showToast?.('Please select a location first', 'warning');
      return;
    }

    try {
      setFetchingDirections(true);
      
      const startLocationId = mappedInSettings?.mappedInStartLocation || null;
      const startLocationName = mappedInSettings?.mappedInStartLocationName || null;
      
      console.log('[EnhancedTextDirectionConfig] Fetching directions for:', {
        externalId: selectedLocation.externalId,
        spaceName: selectedLocation.spaceName || selectedLocation.name?.en,
        startLocationId,
        startLocationName,
      });

      const result = await getMappedInDirections(
        selectedLocation.externalId,
        userId,
        selectedLocation.spaceName || selectedLocation.name?.en,
        startLocationId,
        startLocationName
      );

      if (result && result.success) {
        const rawDirections = removeSSMLTags(result.text);
        setSampleDirections(rawDirections);
        showToast?.('Directions fetched successfully!', 'success');
      } else {
        showToast?.('Failed to fetch directions: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error fetching MappedIn directions:', error);
      showToast?.('Failed to fetch directions: ' + error.message, 'error');
    } finally {
      setFetchingDirections(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.put(`/api/wayfinder/enhanced-text-config/${userId}`, config);
      if (response.data.success) {
        showToast?.('Configuration saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      showToast?.('Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const response = await axios.post(`/api/wayfinder/enhanced-text-config/${userId}/test`, {
        sampleDirections,
      });
      if (response.data.success) {
        setTestResult(response.data.data);
        showToast?.('Test completed successfully!', 'success');
      }
    } catch (error) {
      console.error('Error testing config:', error);
      showToast?.('Test failed: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default configuration?')) {
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(`/api/wayfinder/enhanced-text-config/${userId}/reset`);
      if (response.data.success) {
        setConfig(response.data.data);
        showToast?.('Configuration reset to defaults', 'success');
      }
    } catch (error) {
      console.error('Error resetting config:', error);
      showToast?.('Failed to reset configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PsychologyIcon sx={{ fontSize: 40, color: '#fff' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mb: 0.5 }}>
                AI Enhancement Configuration
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Configure OpenAI settings for enhancing step-by-step directions
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => handleConfigChange('enabled', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#fff',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#fff',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>

      {/* API Key Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <KeyIcon sx={{ color: buttonColor }} />
            <Typography variant="h6" fontWeight={600}>
              API Key Configuration
            </Typography>
          </Box>

          {/* Toggle Button Group for API Key Source */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
              API Key Source
            </Typography>
            <ToggleButtonGroup
              value={config.apiKeySource}
              exclusive
              onChange={(e, value) => value && handleConfigChange('apiKeySource', value)}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  py: 2,
                  textTransform: 'none',
                  border: '1px solid #e0e0e0',
                  '&.Mui-selected': {
                    bgcolor: `${buttonColor}15`,
                    borderColor: buttonColor,
                    color: buttonColor,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: `${buttonColor}25`,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="admin">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminIcon />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" fontWeight={600}>
                      Admin-Assigned
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use administrator key
                    </Typography>
                  </Box>
                </Box>
              </ToggleButton>
              <ToggleButton value="custom">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" fontWeight={600}>
                      Custom Key
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use your own key
                    </Typography>
                  </Box>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Admin Key Selection */}
          {config.apiKeySource === 'admin' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Select Admin API Key
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={config.adminApiKeyId || ''}
                  onChange={(e) => {
                    handleConfigChange('adminApiKeyId', e.target.value);
                    // Auto-update model from selected key
                    const selectedKey = adminKeys.find(k => k._id === e.target.value);
                    if (selectedKey && selectedKey.model) {
                      handleConfigChange('model', selectedKey.model);
                    }
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select an API key</em>
                  </MenuItem>
                  {adminKeys.map((key) => (
                    <MenuItem key={key._id} value={key._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{key.name}</Typography>
                        {key.model && (
                          <Chip 
                            label={getModelLabel(key.model)} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {adminKeys.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No admin API keys available. Please contact your administrator.
                </Alert>
              )}
            </Box>
          )}

          {/* Custom Key Input */}
          {config.apiKeySource === 'custom' && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Enter Your OpenAI API Key
              </Typography>
              <TextField
                fullWidth
                type="password"
                placeholder="sk-..."
                value={config.customApiKey}
                onChange={(e) => handleConfigChange('customApiKey', e.target.value)}
                helperText="Your API key is stored securely and never shared"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <SettingsIcon sx={{ color: buttonColor }} />
            <Typography variant="h6" fontWeight={600}>
              Model Configuration
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              OpenAI Model
            </Typography>
            <FormControl fullWidth>
              <Select
                value={config.model}
                onChange={(e) => handleConfigChange('model', e.target.value)}
              >
                {ALL_OPENAI_MODELS.map((model) => (
                  <MenuItem key={model.value} value={model.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {model.label}
                      </Typography>
                      {model.description && (
                        <Typography variant="caption" color="text.secondary">
                          {model.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Temperature: {config.temperature}
            </Typography>
            <Slider
              value={config.temperature}
              onChange={(e, value) => handleConfigChange('temperature', value)}
              min={0}
              max={2}
              step={0.1}
              marks={[
                { value: 0, label: '0 (Precise)' },
                { value: 1, label: '1 (Balanced)' },
                { value: 2, label: '2 (Creative)' },
              ]}
              sx={{
                color: buttonColor,
                '& .MuiSlider-thumb': {
                  backgroundColor: buttonColor,
                },
                '& .MuiSlider-track': {
                  backgroundColor: buttonColor,
                },
                '& .MuiSlider-rail': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Higher values make output more creative and varied, lower values make it more focused and deterministic
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Custom Prompt */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CodeIcon sx={{ color: buttonColor }} />
            <Typography variant="h6" fontWeight={600}>
              Custom Prompt
            </Typography>
            <Chip label="Advanced" size="small" color="warning" />
          </Box>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            Use <code>{'{rawDirections}'}</code> as a placeholder for the original directions text
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={12}
            value={config.customPrompt}
            onChange={(e) => handleConfigChange('customPrompt', e.target.value)}
            placeholder="Enter your custom prompt here..."
            variant="outlined"
          />
        </CardContent>
      </Card>

      {/* Test Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setShowTestSection(!showTestSection)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlayArrowIcon sx={{ color: buttonColor }} />
              <Typography variant="h6" fontWeight={600}>
                Test Configuration
              </Typography>
            </Box>
            <IconButton>
              <ExpandMoreIcon
                sx={{
                  transform: showTestSection ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              />
            </IconButton>
          </Box>

          <Collapse in={showTestSection}>
            <Divider sx={{ my: 2 }} />
            
            {/* Location Selector for MappedIn Directions */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ fontSize: 18 }} />
                Select Location (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Choose a location to fetch MappedIn directions automatically
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  fullWidth
                  options={locations}
                  value={selectedLocation}
                  onChange={(event, newValue) => setSelectedLocation(newValue)}
                  getOptionLabel={(option) => {
                    const name = option.name?.en || option.name || 'Unnamed Location';
                    const externalId = option.externalId ? ` (${option.externalId})` : '';
                    return `${name}${externalId}`;
                  }}
                  loading={loadingLocations}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search locations..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {loadingLocations ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name?.en || option.name || 'Unnamed Location'}
                        </Typography>
                        {option.externalId && (
                          <Typography variant="caption" color="text.secondary">
                            External ID: {option.externalId}
                          </Typography>
                        )}
                        {option.spaceName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Space: {option.spaceName}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  noOptionsText="No locations found"
                />
                
                <Button
                  variant="outlined"
                  startIcon={fetchingDirections ? <CircularProgress size={16} /> : <MapIcon />}
                  onClick={handleFetchMappedInDirections}
                  disabled={!selectedLocation || fetchingDirections}
                  sx={{
                    minWidth: 180,
                    borderColor: buttonColor,
                    color: buttonColor,
                    '&:hover': {
                      borderColor: buttonColor,
                      bgcolor: `${buttonColor}10`,
                    },
                  }}
                >
                  {fetchingDirections ? 'Fetching...' : 'Get Directions'}
                </Button>
              </Box>
              
              {selectedLocation && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Selected:</strong> {selectedLocation.name?.en || selectedLocation.name}
                    {selectedLocation.externalId && ` • External ID: ${selectedLocation.externalId}`}
                  </Typography>
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Sample Directions
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Edit the directions below or use the location selector above to fetch MappedIn directions
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={sampleDirections}
              onChange={(e) => setSampleDirections(e.target.value)}
              placeholder="Enter sample directions to test..."
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              startIcon={testing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
              onClick={handleTest}
              disabled={testing || !sampleDirections}
              sx={{
                bgcolor: buttonColor,
                '&:hover': { bgcolor: buttonColor, opacity: 0.9 },
              }}
            >
              {testing ? 'Testing...' : 'Test Enhancement'}
            </Button>

            {testResult && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Original:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2">{testResult.original}</Typography>
                </Paper>

                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Enhanced:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                  <Typography variant="body2">{testResult.enhanced}</Typography>
                </Paper>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            bgcolor: buttonColor,
            '&:hover': { bgcolor: buttonColor, opacity: 0.9 },
            minWidth: 150,
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default EnhancedTextDirectionConfig;
